import { usePlayer } from '@/stores/playback.ts';
import { useUi } from '@/stores/ui.ts';
import { ChannelLogo } from '@/ui/ChannelLogo.tsx';
import { Focusable } from '@/ui/Focusable.tsx';
import { player } from '@/lib/player.ts';
import { isTv } from '@/lib/native.ts';

/**
 * What you look at while a channel starts.
 *
 * The server deliberately holds a cold tune for a few seconds so the player
 * starts with cushion instead of pinned to the live edge (see
 * http/live-window.ts). That is the right trade, but only if the wait looks
 * like the app working rather than the app hanging — an unexplained black
 * screen for eight seconds is indistinguishable from a crash, and the second
 * press of the remote during it makes everything worse.
 */
export function TuningCard() {
  const { status, channel, error, trail } = usePlayer();
  const { view, zapping } = useUi();

  if (view === 'browse' || zapping) return null;
  if (status !== 'loading' && !error) return null;

  const showing = channel;
  if (!showing) return null;

  return (
    <div className="tuning">
      <div className="tuning-logo"><ChannelLogo channel={showing} className="logo-inner" /></div>
      <div className="tuning-name">{showing.name}</div>
      {error
        ? (
          <>
            <div className="tuning-state error">{error}</div>
            {/*
              A BUTTON, NOT AN INSTRUCTION.
              
              This said "Press OK to try again" on every client, which is
              remote language: in a browser there is no OK key, this card is
              not clickable, and it covers the mini player's own Retry — so
              the one thing the screen told you to do could not be done. The
              television keeps the wording, because there the card IS focused
              and OK is the right key; everywhere else it gets something to
              click.
            */}
            {isTv()
              ? <div className="tuning-hint">Press OK to try again</div>
              : (
                <Focusable
                  id="tuning-retry"
                  autoFocus
                  className="btn primary tuning-retry"
                  onSelect={() => player.retry()}
                >
                  Try again
                </Focusable>
              )}

            {/*
              WHAT THE PLAYER SAW, on the screen rather than in a console.
              
              Three rounds of this bug were diagnosed from server logs, which
              can say what was sent and nothing about what the browser did
              with it. This is that missing half, put where somebody will
              actually read it — and the Copy button exists because the useful
              thing to do with it is paste it to whoever is fixing this.
            */}
            {trail.length > 0 && !isTv() && (
              <details className="tuning-trail">
                <summary>What happened</summary>
                <pre>{trail.join('\n')}</pre>
                <button
                  type="button"
                  className="btn"
                  onClick={() => { void navigator.clipboard?.writeText(trail.join('\n')); }}
                >
                  Copy
                </button>
              </details>
            )}
          </>
        )
        : <div className="tuning-state">Tuning<span className="dots" /></div>}
    </div>
  );
}
